'use client';

/**
 * Analytics consent banner for public pages (landing, /commands, /login).
 * Shown only on first visit when no consent cookie exists.
 *
 * GDPR / ePrivacy compliance posture:
 *   - `posthog.init` defaults to opted-out unless the consent cookie is
 *     explicitly "accepted" (see initPostHog in posthog-provider.js).
 *   - Visitor's choice is persisted in a first-party cookie `shoosh_analytics_consent`
 *     with a 1-year Max-Age, Lax SameSite, Path=/.
 *   - On Accept → `posthog.opt_in_capturing()` + cookie set.
 *   - On Decline → `posthog.opt_out_capturing()` + cookie set.
 *   - Authenticated users' dashboard provider calls `opt_in_capturing()`
 *     regardless of cookie, under the "service relationship" rationale
 *     (documented in the privacy policy once drafted).
 *
 * Banner is a client component — server-side can't read or set cookies
 * for the useEffect-driven state transition anyway. Render is suppressed
 * if the user has already made a choice (hides immediately on mount if
 * cookie already present, so returning visitors don't see it again).
 */
import { useEffect, useState } from 'react';
import Link from 'next/link';
import posthog from 'posthog-js';

export const CONSENT_COOKIE = 'shoosh_analytics_consent';
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

export function readConsent() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeConsent(value) {
  if (typeof document === 'undefined') return;
  const parts = [
    `${CONSENT_COOKIE}=${encodeURIComponent(value)}`,
    `Max-Age=${CONSENT_MAX_AGE_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
  ];
  // Append Secure on HTTPS so the cookie isn't readable/writable over
  // plain HTTP if an attacker ever forces a downgrade before HSTS kicks
  // in. Skip on http:// localhost dev — browsers reject Secure cookies
  // on non-HTTPS origins.
  if (typeof window !== 'undefined' && window.location?.protocol === 'https:') {
    parts.push('Secure');
  }
  document.cookie = parts.join('; ');
}

export default function ConsentBanner() {
  // Three-state machine: 'loading' (SSR + first render, suppresses flash),
  // 'pending' (cookie absent, show banner), 'resolved' (user has chosen).
  const [state, setState] = useState('loading');

  useEffect(() => {
    const existing = readConsent();
    if (existing === 'accepted') {
      try {
        posthog.opt_in_capturing();
      } catch (err) {
        console.warn('[posthog] opt_in failed', err);
      }
      setState('resolved');
    } else if (existing === 'declined') {
      try {
        posthog.opt_out_capturing();
      } catch (err) {
        console.warn('[posthog] opt_out failed', err);
      }
      setState('resolved');
    } else {
      setState('pending');
    }
  }, []);

  function accept() {
    writeConsent('accepted');
    try {
      posthog.opt_in_capturing();
    } catch (err) {
      console.warn('[posthog] opt_in failed', err);
    }
    setState('resolved');
  }

  function decline() {
    writeConsent('declined');
    try {
      posthog.opt_out_capturing();
    } catch (err) {
      console.warn('[posthog] opt_out failed', err);
    }
    setState('resolved');
  }

  if (state !== 'pending') return null;

  return (
    <div
      role="dialog"
      aria-labelledby="consent-heading"
      className="fixed bottom-4 left-4 right-4 z-50 max-w-2xl mx-auto bg-gum-gray-900 text-gum-white border-2 border-gum-gray-700 rounded-xl p-4 shadow-2xl"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="text-sm">
          <p id="consent-heading" className="font-semibold mb-1">
            Cookies &amp; analytics
          </p>
          <p className="text-gum-gray-400 text-xs">
            Shoosh uses analytics to understand how the product is used and to fix bugs.{' '}
            <Link href="/privacy" className="text-gum-pink hover:underline">
              Learn more
            </Link>
            .
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={decline}
            className="px-4 py-2 text-xs font-bold border-2 border-gum-gray-700 rounded-lg hover:border-gum-gray-500 transition-colors"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={accept}
            className="px-4 py-2 text-xs font-bold bg-gum-pink text-black rounded-lg hover:brightness-110 transition-all"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
